import {
  Box,
  Card,
  CardHeader,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import VideoBox from "@volatile/pages/PhysicianReview/components/VideoBox";
import {
  useWebApiV1ListWalkthroughAttachmentsQuery,
  WalkthroughAttachmentOut,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import SynchronizedVideoPlayer from "./SynchronizedVideoPlayer";

interface VideoWalkthroughProps {
  orderId: number;
  isComparisonMode?: boolean;
  autoplay?: boolean;
  muted?: boolean;
}

interface NotAvailablePlaceholderProps {
  message: string;
  minHeight?: number;
}

const NotAvailablePlaceholder = ({
  message,
  minHeight = 120,
}: NotAvailablePlaceholderProps) => (
  <Stack
    justifyContent="center"
    alignItems="center"
    minHeight={minHeight}
    border="1px dashed"
    borderColor="divider"
    borderRadius={1}
  >
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Stack>
);

interface PlanVideoSectionProps {
  planLabel: string;
  video: WalkthroughAttachmentOut | null;
  testId: string;
  autoplay?: boolean;
  muted?: boolean;
}

const PlanVideoSection = ({
  planLabel,
  video,
  testId,
  autoplay = true,
  muted = true,
}: PlanVideoSectionProps) => (
  <Stack flex={1} gap={1}>
    <Typography variant="subtitle2" fontWeight="bold">
      {planLabel}
    </Typography>
    {video ? (
      <Box sx={{ width: "100%", height: "auto" }}>
        <VideoBox
          src={video.file}
          data-testid={testId}
          autoplay={autoplay}
          muted={muted}
        />
      </Box>
    ) : (
      <NotAvailablePlaceholder message={`${planLabel} video not available`} />
    )}
  </Stack>
);

const VideoWalkthrough = ({
  orderId,
  isComparisonMode = false,
  autoplay = true,
  muted = true,
}: VideoWalkthroughProps) => {
  const [synchronizeEnabled, setSynchronizeEnabled] = useState(true);

  const {
    data: walkthroughAttachmentsData,
    isLoading: walkthroughAttachmentsLoading,
  } = useWebApiV1ListWalkthroughAttachmentsQuery(
    orderId !== undefined
      ? {
          orderId,
        }
      : skipToken,
  );

  const walkthroughs = walkthroughAttachmentsData?.filter(
    (w) => w.fileName.search(/\.(mp4|mov|webm)$/i) !== -1,
  );

  const walkthroughsAvailable = walkthroughs && walkthroughs.length > 0;

  // Smart video selection logic
  const findVideoByName = (baseName: string) => {
    return (
      walkthroughs?.find((video) => {
        const fileNameWithoutExt = video.fileName
          .replace(/\.[^/.]+$/, "")
          .toLowerCase();
        return fileNameWithoutExt === baseName.toLowerCase();
      }) || null
    );
  };

  // Look for specific plan videos first
  let planAVideo = findVideoByName("plan_a_walkthrough");
  let planBVideo = findVideoByName("plan_b_walkthrough");

  // Fallback logic
  if (!planAVideo && !planBVideo && walkthroughs && walkthroughs.length > 0) {
    // Neither specific video found, use any available video for both
    planAVideo = walkthroughs[0];
    planBVideo = walkthroughs[0];
  } else if (planAVideo && !planBVideo) {
    // Only Plan A found, use it for both
    planBVideo = planAVideo;
  } else if (!planAVideo && planBVideo) {
    // Only Plan B found, use it for both
    planAVideo = planBVideo;
  }
  // If both found, use them as-is

  if (walkthroughAttachmentsLoading) {
    return (
      <Card variant="outlined">
        <CardHeader
          title={
            <Typography variant="h6">Dose Distribution Walkthrough</Typography>
          }
          subheader="High resolution screen recordings displaying isodose distributions"
        />
        <Divider />
        <NotAvailablePlaceholder message="Loading videos..." minHeight={300} />
      </Card>
    );
  }

  if (!walkthroughsAvailable) {
    return (
      <Card variant="outlined">
        <CardHeader
          title={
            <Typography variant="h6">Dose Distribution Walkthrough</Typography>
          }
          subheader="High resolution screen recordings displaying isodose distributions"
        />
        <Divider />
        <NotAvailablePlaceholder
          message="No dose distribution walkthrough available"
          minHeight={300}
        />
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Typography variant="h6">Dose Distribution Walkthrough</Typography>
        }
        subheader="High resolution screen recordings displaying isodose distributions"
      />
      <Divider />
      <Stack padding={2} paddingBottom={isComparisonMode ? 0.5 : 2} gap={2}>
        {isComparisonMode ? (
          // Comparison mode: Show two synchronized videos side by side
          planAVideo && planBVideo ? (
            <SynchronizedVideoPlayer
              planAVideo={planAVideo}
              planBVideo={planBVideo}
              planALabel="Plan A"
              planBLabel="Plan B"
              planATestId="plan-a-video"
              planBTestId="plan-b-video"
              synchronizeEnabled={synchronizeEnabled}
              onSynchronizeChange={setSynchronizeEnabled}
              muted={muted}
            />
          ) : (
            <Stack direction="row" gap={2}>
              <PlanVideoSection
                planLabel="Plan A"
                video={planAVideo}
                testId="plan-a-video"
                autoplay={autoplay}
                muted={muted}
              />
              <PlanVideoSection
                planLabel="Plan B"
                video={planBVideo}
                testId="plan-b-video"
                autoplay={autoplay}
                muted={muted}
              />
            </Stack>
          )
        ) : (
          // Single plan mode: Show one video
          <Stack gap={1}>
            {planAVideo ? (
              <VideoBox
                src={planAVideo.file}
                data-testid="plan-walkthrough-video"
                autoplay={autoplay}
                muted={muted}
              />
            ) : (
              <NotAvailablePlaceholder
                message="Plan walkthrough video not available"
                minHeight={200}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default VideoWalkthrough;
